import type {CohortPromoCodeUsageData} from '@cohort/shared/apps/cohort/perks/promoCode';
import {
  CohortPromoCodeAccessDataSchema,
  CohortPromoCodeExposedConfigSchema,
} from '@cohort/shared/apps/cohort/perks/promoCode';
import {defaultErrorMessage} from '@cohort/shared/models';
import {isCohortError} from '@cohort/shared/schema/common/errors';
import type {PerkUsageComponentProps} from '@cohort/wallet/apps/PerkIntegration';
import Button from '@cohort/wallet/components/button/Button';
import {useCohortMutation} from '@cohort/wallet/hooks/api/Query';
import useNotify from '@cohort/wallet/hooks/notify';
import {activatePerkAccess, doUsePerkAccess} from '@cohort/wallet/lib/Endpoints';
import {trackError} from '@cohort/wallet/lib/Tracking';
import {Check, Copy} from '@phosphor-icons/react';
import React, {Fragment, useEffect, useMemo, useRef, useState} from 'react';
import {useTranslation} from 'react-i18next';
import {isEmpty} from 'remeda';

export const CohortPromoCodePerkUsageComponent: React.FC<PerkUsageComponentProps> = props => {
  const {perk, onPerkUsageSuccess, perkAccess} = props;
  const perkConfig = CohortPromoCodeExposedConfigSchema.parse(perk.integration?.config);
  const perkAccessData = CohortPromoCodeAccessDataSchema.parse(perkAccess.data);

  const [codeCopied, setCodeCopied] = useState(false);
  const [maxUsagesReached, setMaxUsagesReached] = useState(false);
  const [promoCode, setPromoCode] = useState<string | null>(
    'code' in perkAccessData ? perkAccessData.code : null
  );
  const initialized = useRef(false);
  const {t} = useTranslation('app-cohort', {
    keyPrefix: 'perkIntegrations.promoCode.perkUsageComponent',
  });
  const notify = useNotify();

  // @Devs - If {{code}} is present in the redirectionUrl, replace it by the code.
  const formattedRedirectionUrl = useMemo(() => {
    if (!promoCode) {
      return null;
    }
    return perkConfig.redirectionUrl.replace('{{code}}', promoCode);
  }, [perkConfig.redirectionUrl, promoCode]);

  const {isLoading, mutate: doUsePerkAccessMutation} = useCohortMutation({
    mutationFn: async () => {
      if (!promoCode || !formattedRedirectionUrl) {
        return;
      }

      return doUsePerkAccess(perkAccess.id, null, {
        type: perkConfig.type,
        code: promoCode,
        redirectionUrl: formattedRedirectionUrl,
      } satisfies CohortPromoCodeUsageData);
    },
    onSuccess: () => onPerkUsageSuccess(),
  });

  const {mutate: activatePromoCode} = useCohortMutation({
    mutationFn: async () => activatePerkAccess(perkAccess.id),
    onSuccess: ({data}) => {
      const activatedPromoCodeData = CohortPromoCodeAccessDataSchema.parse(data);
      if ('code' in activatedPromoCodeData && typeof activatedPromoCodeData.code === 'string')
        setPromoCode(activatedPromoCodeData.code);
      else setPromoCode(null);
    },
    onError: err => {
      if (
        isCohortError(err, 'app-framework.activate-perk-access-error') &&
        'cohortErrorCode' in err.context &&
        err.context.cohortErrorCode === 'perk.usages-limit-reached'
      ) {
        setMaxUsagesReached(true);
        return;
      }
      notify('error', defaultErrorMessage);
    },
  });

  useEffect(() => {
    if (initialized.current || !isEmpty(perkAccessData) || perkAccess.canBeUsed === false) {
      return;
    }

    activatePromoCode();
    return () => {
      initialized.current = true;
    };
  }, [perkAccess.canBeUsed, perkAccessData, activatePromoCode]);

  const handleCopyCodeClick = (): void => {
    if (!promoCode) {
      return;
    }
    navigator.clipboard.writeText(promoCode).catch(trackError);
    setCodeCopied(true);
    doUsePerkAccessMutation();
  };

  const handleUseCode = (): void => {
    if (!formattedRedirectionUrl) {
      return;
    }
    doUsePerkAccessMutation();
    window.open(formattedRedirectionUrl, '_blank');
  };

  const getCtaText = (): string => {
    return perk.useCta ?? t('useMyCode');
  };

  return (
    <div className="flex w-full flex-col space-y-8">
      {maxUsagesReached ? (
        <div className="flex w-full flex-col space-y-4 rounded-[--xps-card-border-radius] bg-[--xps-card-background-color] p-4 text-center">
          <p>{t('maxUsagesReached')}</p>
        </div>
      ) : (
        <Fragment>
          <div className="flex w-full flex-col space-y-4 rounded-[--xps-card-border-radius] bg-[--xps-card-background-color] p-4 text-center">
            <div className="flex flex-col gap-2">
              <p className="text-lg font-bold">{t('promoCodeTitle')}</p>
              <h2 className="text-3xl font-bold">{promoCode}</h2>
            </div>

            <Button
              variant="secondary"
              size="small"
              type="button"
              onClick={handleCopyCodeClick}
              tracking={{namespace: 'perks.usage.copyPromoCode'}}
              loading={isLoading || !promoCode}
            >
              {codeCopied ? (
                <div className="flex items-center gap-x-1">
                  <Check size={16} className="text-[--xps-p-color]" />
                  {t('copied')}
                </div>
              ) : (
                <div className="flex items-center gap-x-1">
                  <Copy size={16} className="text-[--xps-p-color]" />
                  {t('copy')}
                </div>
              )}
            </Button>
          </div>
          <Button
            variant="primary"
            type="button"
            onClick={handleUseCode}
            tracking={{namespace: 'perks.usage.usePromoCode'}}
            loading={isLoading || !promoCode}
          >
            {getCtaText()}
          </Button>
        </Fragment>
      )}
    </div>
  );
};
